import { useEffect, useId, useRef } from "react";

import { cn } from "../lib/cn";

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  /** Visible label, rendered to the right of the box. Clicking it toggles. */
  label?: string;
  /** Mixed state: draws a dash instead of the tick and sets the DOM property. */
  indeterminate?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * A real `<input type="checkbox">` with the paint stripped off: espresso box,
 * pen-tick mark, optional mixed state. Native semantics and keyboard behaviour
 * are untouched.
 */
export function Checkbox({
  label,
  indeterminate = false,
  id,
  className,
  disabled,
  ref,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const innerRef = useRef<HTMLInputElement | null>(null);

  // `indeterminate` is a DOM property, not an attribute, so it has to be set
  // imperatively on every change.
  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const setRef = (node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as React.RefObject<HTMLInputElement | null>).current = node;
    }
  };

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "inline-flex items-start gap-2 font-body text-sm",
        disabled
          ? "cursor-not-allowed text-faint"
          : "cursor-pointer text-ink-body",
      )}
    >
      <span className="relative mt-px inline-grid size-4 shrink-0 place-items-center">
        <input
          ref={setRef}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          className={cn(
            "peer size-4 appearance-none rounded-[2px] border bg-card",
            "transition duration-150 ease-paper",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            "checked:bg-ink indeterminate:bg-ink",
            disabled
              ? "cursor-not-allowed border-rule bg-recess checked:bg-faint indeterminate:bg-faint"
              : "cursor-pointer border-ink",
            className,
          )}
          {...rest}
        />
        {/* Pen tick: short down-stroke, long flicked up-stroke, slight overshoot. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          className={cn(
            "pointer-events-none absolute hidden size-3.5 text-paper",
            "peer-checked:block peer-indeterminate:hidden",
          )}
        >
          <path
            d="M3.2 8.4 L6.3 11.6 L12.9 3.9"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute hidden h-[1.5px] w-2 rounded-full bg-paper",
            "peer-indeterminate:block",
          )}
        />
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}
